// Master language config for the site's URL-localized routes (e.g.
// `/es/contact`, `/zh/faq`). Adding a language here:
//   1) auto-generates the React Router entries in App.js for every page
//      that opted in (the language list is consumed via the spread in
//      App.js: `NON_DEFAULT_SITE_LANGUAGE_CODES.map(...)`),
//   2) emits hreflang tags for that language in every <SEO> page,
//   3) and triggers the per-language SEO HTML build for that language.
//
// `htmlDir: 'rtl'` is honored by the static SEO generator
// (scripts/generateStaticSeoPages.js → upsertHtmlTag).
export const SITE_LANGUAGE_META = {
  en: { htmlLang: 'en', ogLocale: 'en_US' },
  fr: { htmlLang: 'fr', ogLocale: 'fr_FR' },
  de: { htmlLang: 'de', ogLocale: 'de_DE' },
  es: { htmlLang: 'es', ogLocale: 'es_ES' },
  it: { htmlLang: 'it', ogLocale: 'it_IT' },
  pt: { htmlLang: 'pt', ogLocale: 'pt_PT' },
  zh: { htmlLang: 'zh-CN', ogLocale: 'zh_CN' },
  ar: { htmlLang: 'ar', ogLocale: 'ar_SA', htmlDir: 'rtl' },
};

export const SITE_LANGUAGE_CODES = Object.keys(SITE_LANGUAGE_META);
export const NON_DEFAULT_SITE_LANGUAGE_CODES = SITE_LANGUAGE_CODES.filter((code) => code !== 'en');

const normalizePath = (path = '/') => {
  const raw = String(path || '').trim();
  if (!raw || raw === '/') {
    return '/';
  }
  const withLeadingSlash = raw.startsWith('/') ? raw : `/${raw}`;
  return withLeadingSlash.replace(/\/+$/, '') || '/';
};

export const inferSiteLanguageCode = (pathname = '/') => {
  const normalized = normalizePath(pathname);
  const firstSegment = normalized.split('/').filter(Boolean)[0] || '';
  return SITE_LANGUAGE_CODES.includes(firstSegment) ? firstSegment : 'en';
};

export const stripSiteLanguagePrefix = (pathname = '/') => {
  const normalized = normalizePath(pathname);
  const segments = normalized.split('/').filter(Boolean);

  if (segments.length === 0) {
    return '/';
  }

  const firstSegment = segments[0];
  if (!NON_DEFAULT_SITE_LANGUAGE_CODES.includes(firstSegment)) {
    return normalized;
  }

  const stripped = `/${segments.slice(1).join('/')}`;
  return stripped === '/' ? '/' : stripped.replace(/\/+$/, '') || '/';
};

export const withSiteLanguagePrefix = (path = '/', languageCode = 'en') => {
  const normalized = stripSiteLanguagePrefix(path);
  if (!NON_DEFAULT_SITE_LANGUAGE_CODES.includes(languageCode)) {
    return normalized;
  }

  if (normalized === '/') {
    return `/${languageCode}`;
  }

  return `/${languageCode}${normalized}`;
};

export const getSiteLanguageCanonicalPath = (basePath = '/') => normalizePath(basePath);

// Allow indexing for all languages — the whole point of localized URLs
// is to rank in Google in each language. The previous `noindex,follow`
// for non-English was a defensive default from when only fr/de existed
// and the translations weren't wired through the React app yet. Now
// that every page renders in the right language and hreflang clusters
// link the variants together, all language variants should index.
export const getSiteLanguageRobots = (_languageCode = 'en') =>
  'index,follow,max-image-preview:large,max-snippet:-1';

// Emit hreflang for every supported language plus x-default. The
// generated alternates link the n language variants together so
// Google serves the right one per searcher.
export const buildSiteLanguageAlternates = (basePath = '/') => {
  const canonical = getSiteLanguageCanonicalPath(basePath);
  const alternates = SITE_LANGUAGE_CODES.map((code) => ({
    hrefLang: code,
    path: withSiteLanguagePrefix(canonical, code),
  }));
  alternates.push({ hrefLang: 'x-default', path: canonical });
  return alternates;
};

export const getSiteLanguageMeta = (languageCode = 'en') =>
  SITE_LANGUAGE_META[languageCode] || SITE_LANGUAGE_META.en;
